import { createSlice } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import { decode } from 'iconv-lite'
import { v4 as uuidV4 } from 'uuid'

import * as API from 'api/csv_uploader/csv_uploader'
import type { Bucket } from 'api/csv_uploader/types'

import { handleApiError, putUploadUrl } from 'slices/utils'

import type { PayloadAction } from '@reduxjs/toolkit'
import type { AxiosError } from 'axios'
import type { AppThunk, RootState } from 'store'

type CsvUploaderState = {
  isRequesting: boolean
  errorMessage: string
  buckets: Bucket[]
  isBucketsLoading: boolean
  bucketsErrorMessage: string
}

const initialState: CsvUploaderState = {
  isRequesting: false,
  errorMessage: '',
  buckets: [],
  isBucketsLoading: false,
  bucketsErrorMessage: '',
}

export const csvUploaderSlice = createSlice({
  name: 'csvUploader',
  initialState,
  reducers: {
    startRequest: state => {
      state.isRequesting = true
      state.errorMessage = ''
    },
    apiFailure: (state, action: PayloadAction<{ errorMessage: string }>) => {
      state.isRequesting = false
      state.errorMessage = action.payload.errorMessage
    },
    startBucketsRequest: state => {
      state.isBucketsLoading = true
      state.bucketsErrorMessage = ''
    },
    getBucketsSuccess: (state, action: PayloadAction<Bucket[]>) => {
      state.isBucketsLoading = false
      state.buckets = action.payload
    },
    bucketsRequestFailure: (state, action: PayloadAction<{ errorMessage: string }>) => {
      state.isBucketsLoading = false
      state.bucketsErrorMessage = action.payload.errorMessage
    },
    requestFinished: state => {
      state.isRequesting = false
    },
  },
})

export const {
  startRequest,
  apiFailure,
  startBucketsRequest,
  getBucketsSuccess,
  bucketsRequestFailure,
  requestFinished,
} = csvUploaderSlice.actions

const CHUNK_SIZE = 10000

const splitCsvIntoChunks = (buffer: ArrayBuffer): ArrayBuffer[] => {
  // SHIFT-JIS → Unicode文字列に変換（csv-uploaderと同様）
  const text = decode(new Uint8Array(buffer) as unknown as Buffer, 'Shift_JIS')
  const lines = text.split(/\r?\n/)

  // 末尾の空行を除去
  if (lines[lines.length - 1] === '') {
    lines.pop()
  }

  // ヘッダー1行のみ or 空ファイルはそのまま返す
  if (lines.length <= 1) {
    return [new TextEncoder().encode(lines.join('\n')).buffer]
  }

  const [header, ...dataRows] = lines
  const encoder = new TextEncoder()

  // UTF-8としてチャンクに分割
  const chunks: ArrayBuffer[] = []
  for (let i = 0; i < dataRows.length; i += CHUNK_SIZE) {
    const chunkText = [header, ...dataRows.slice(i, i + CHUNK_SIZE)].join('\n')
    chunks.push(encoder.encode(chunkText).buffer)
  }
  return chunks
}

export const getBuckets = (): AppThunk => async dispatch => {
  dispatch(startBucketsRequest())
  try {
    const res = await API.getBuckets()
    dispatch(getBucketsSuccess(res.buckets))
  } catch (res) {
    // getBucketsの失敗はaxiosのresponse interceptorがGETリクエストとして
    // グローバルにエラーダイアログを表示するため、ここではisRequesting/errorMessageを変更しない
    handleApiError(res as AxiosError, dispatch, bucketsRequestFailure)
  }
}

export const uploadCsvFile =
  (bucketDirectory: string, bucketName: string, csvContent: ArrayBuffer): AppThunk =>
  async dispatch => {
    dispatch(startRequest())
    const uploadDateTime = dayjs().format('YYYYMMDD_HHmmss')
    try {
      const chunks = splitCsvIntoChunks(csvContent)
      for (const chunk of chunks) {
        const fileName = `${bucketName}_${uploadDateTime}_${uuidV4()}.csv`
        const res = await API.getUploadUrl({ bucketDirectory, fileName })
        await putUploadUrl(res.uploadUrl, chunk)
      }
      dispatch(requestFinished())
    } catch (res) {
      handleApiError(res as AxiosError, dispatch, apiFailure)
    }
  }

export const deleteWorkResults =
  (bucketDirectory: string, startDate: string, endDate: string): AppThunk =>
  async dispatch => {
    dispatch(startRequest())
    try {
      await API.deleteWorkResults({ bucketDirectory, startDate, endDate })
      dispatch(requestFinished())
    } catch (res) {
      handleApiError(res as AxiosError, dispatch, apiFailure)
    }
  }

export const selectCsvUploaderStatus = (state: RootState) => ({ ...state.csvUploader })

export default csvUploaderSlice.reducer
